import './CabinetLayout.scss';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  Home,
  LogOut,
  MessageCircle,
  Users,
  User,
  LifeBuoy,
  Briefcase,
  Menu as MenuIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../i18n';
import { get } from '../../services/api';
import Avatar from '../Avatar';
import Breadcrumbs from '../Breadcrumbs';
import LangSwitch from '../LangSwitch';

interface NavLinkItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
}

export default function CabinetLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      get<{ unread: number }>('/me/notifications/unread-count/')
        .then((r) => { if (!cancelled) setUnread(Number(r?.unread) || 0); })
        .catch(() => undefined);
    };
    load();
    const id = window.setInterval(load, 30000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);

  const nav: NavLinkItem[] = useMemo(() => ([
    { to: '/cabinet', label: t('cabinet.nav.dashboard'), icon: <Home size={18} />, end: true },
    { to: '/cabinet/orders', label: t('cabinet.nav.orders'), icon: <Briefcase size={18} /> },
    { to: '/cabinet/team', label: t('cabinet.nav.team'), icon: <Users size={18} /> },
    { to: '/cabinet/notifications', label: t('cabinet.nav.notifications'), icon: <Bell size={18} /> },
    { to: '/cabinet/support', label: t('cabinet.nav.support'), icon: <LifeBuoy size={18} /> },
    { to: '/cabinet/profile', label: t('cabinet.nav.profile'), icon: <User size={18} /> },
  ]), [t]);

  const bottom: NavLinkItem[] = useMemo(() => ([
    { to: '/cabinet', label: t('cabinet.nav.dashboard'), icon: <Home size={20} />, end: true },
    { to: '/cabinet/orders', label: t('cabinet.nav.orders'), icon: <Briefcase size={20} /> },
    { to: '/cabinet/notifications', label: t('cabinet.nav.chat'), icon: <MessageCircle size={20} /> },
    { to: '/cabinet/profile', label: t('cabinet.nav.menu'), icon: <MenuIcon size={20} /> },
  ]), [t]);

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const crumbs = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const items: { label: string; href?: string }[] = [{ label: t('cabinet.nav.dashboard'), href: '/cabinet' }];
    if (parts[1]) {
      const key = `cabinet.nav.${parts[1]}`;
      items.push({ label: t(key), href: parts[2] ? `/cabinet/${parts[1]}` : undefined });
    }
    if (parts[2]) items.push({ label: parts[2] });
    return items;
  }, [location.pathname, t]);

  return (
    <div className="cabinet-layout">
      <aside className={['cabinet-layout__sidebar', drawer ? 'cabinet-layout__sidebar--open' : ''].filter(Boolean).join(' ')}>
        <Link to="/cabinet" className="cabinet-layout__logo" onClick={() => setDrawer(false)}>
          <span className="cabinet-layout__logo-text">Ukam IT</span>
        </Link>
        <hr className="divider cabinet-layout__divider" />
        <nav className="cabinet-layout__nav" aria-label={t('cabinet.nav.menu')}>
          {nav.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={() => setDrawer(false)}
              className={({ isActive }) =>
                ['cabinet-layout__nav-item', isActive ? 'cabinet-layout__nav-item--active' : ''].filter(Boolean).join(' ')
              }
            >
              <span className="cabinet-layout__nav-icon" aria-hidden="true">{it.icon}</span>
              <span className="cabinet-layout__nav-label">{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <hr className="divider cabinet-layout__divider" />
        <div className="cabinet-layout__user">
          <Avatar name={user?.full_name || user?.email || ''} size="sm" />
          <div className="cabinet-layout__user-info">
            <div className="cabinet-layout__user-name">{user?.full_name || ''}</div>
            <div className="cabinet-layout__user-email">{user?.email || ''}</div>
          </div>
          <button type="button" className="cabinet-layout__logout" onClick={onLogout} aria-label={t('cabinet.nav.logout')}>
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {drawer ? <div className="cabinet-layout__scrim" onClick={() => setDrawer(false)} role="presentation" /> : null}

      <div className="cabinet-layout__main">
        <header className="cabinet-layout__topbar">
          <button
            type="button"
            className="cabinet-layout__burger"
            onClick={() => setDrawer((v) => !v)}
            aria-label={t('cabinet.nav.menu')}
          >
            <MenuIcon size={20} />
          </button>
          <div className="cabinet-layout__crumbs">
            <Breadcrumbs items={crumbs} />
          </div>
          <div className="cabinet-layout__topright">
            <LangSwitch />
            <Link to="/cabinet/notifications" className="cabinet-layout__bell" aria-label={t('cabinet.nav.notifications')} style={{ position: 'relative' }}>
              <Bell size={18} />
              {unread > 0 && (
                <span
                  aria-label={String(unread)}
                  style={{
                    position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18,
                    padding: '0 5px', borderRadius: 9, background: '#DC2626', color: '#fff',
                    fontSize: 10, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
                    boxShadow: '0 0 0 2px var(--c-surface-1, #fff)',
                  }}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </Link>
            <Avatar name={user?.full_name || user?.email || ''} size="sm" />
          </div>
        </header>
        <main className="cabinet-layout__content">
          <Outlet />
        </main>
        <nav className="cabinet-layout__bottom" aria-label={t('cabinet.nav.menu')}>
          {bottom.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                ['cabinet-layout__bottom-item', isActive ? 'cabinet-layout__bottom-item--active' : ''].filter(Boolean).join(' ')
              }
            >
              <span className="cabinet-layout__bottom-icon" aria-hidden="true">{it.icon}</span>
              <span className="cabinet-layout__bottom-label">{it.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

